import { Link } from "@tanstack/react-location"
import { sortBy } from "lodash-es"
import { Icon, useWindowWidth, type IconType } from "materia"
import type { ComponentProps } from "react"
import { useTranslation } from "react-i18next"
import type { LandingPage } from "../../../graphql"
import { useChatState } from "../../../chat/hooks/useChat"
import { hasUnread } from "../../../chat/runtime/selectors"
import { useAIChatContext } from "../../AIChat/providers/AIChatProvider"
import { useCurrentCompanyContext } from "../CurrentCompanyProvider"
import { NavigationSkeleton } from "./NavigationSkeleton"

import s from "./Navigation.module.scss"

type LinkProps = ComponentProps<typeof NavigationItem>
type LinkWithName = LinkProps & {
  hasBadge?: boolean
  /**
   * A landing page where the tab can be one. Messages cannot: the two
   * concepts only coincided until this tab existed.
   */
  name: LandingPage | "messages"
  isLandingPage: boolean
  isEnabled: boolean
}

type NavigationProps = {
  isLoading: boolean
  showJourneyTab: boolean
  showNewsTab: boolean
  showMessagesTab: boolean
  hasUnreadMessages: boolean
  portalLandingPage: string
  showAIChatButton: boolean
}

export const NavigationConnected = () => {
  const { isAIChatEnabled } = useAIChatContext()
  // Safe here: <ChatProvider> is above the shell. The presentational component
  // takes the flag as a prop, so its stories need no runtime.
  const hasUnreadMessages = useChatState(hasUnread)
  const {
    currentCompany: { chatEnabled, portalFeatureFlags, portalLandingPage },
    isCurrentCompanyPending,
  } = useCurrentCompanyContext()

  if (portalFeatureFlags.includes("disable_top_menu")) return null

  return (
    <Navigation
      isLoading={isCurrentCompanyPending}
      portalLandingPage={portalLandingPage}
      showJourneyTab={portalFeatureFlags.includes("enable_learning_journey")}
      showNewsTab={portalFeatureFlags.includes("enable_portal_news")}
      showMessagesTab={chatEnabled}
      hasUnreadMessages={hasUnreadMessages}
      showAIChatButton={isAIChatEnabled}
    />
  )
}

export const Navigation = ({
  isLoading,
  showJourneyTab,
  showNewsTab,
  showMessagesTab,
  hasUnreadMessages,
  portalLandingPage,
  showAIChatButton,
}: NavigationProps) => {
  const { t } = useTranslation()

  // One filled weight per tab: the active tab is set apart by colour and ring,
  // not by a separate icon.
  const linksWithName: LinkWithName[] = [
    {
      name: "for_you",
      href: "/for-you",
      text: t("navigation.FOR_YOU"),
      icon: "Home",
      isLandingPage: portalLandingPage === "for_you",
      isEnabled: true,
    },
    {
      name: "journey",
      href: "/journey",
      text: t("navigation.JOURNEY"),
      icon: "Map",
      isLandingPage: portalLandingPage === "journey",
      isEnabled: showJourneyTab,
    },
    {
      name: "explore",
      href: "/explore",
      text: t("navigation.EXPLORE"),
      icon: "Explore",
      isLandingPage: portalLandingPage === "explore",
      isEnabled: true,
    },
    {
      name: "news",
      href: "/news",
      text: t("navigation.NEWS"),
      icon: "Newspaper",
      isLandingPage: portalLandingPage === "news",
      isEnabled: showNewsTab,
    },
    {
      name: "messages",
      href: "/messages",
      text: t("navigation.MESSAGES"),
      icon: "Send",
      hasBadge: hasUnreadMessages,
      isLandingPage: false,
      isEnabled: showMessagesTab,
    },
  ]

  const enabledLinks = linksWithName.filter(({ isEnabled }) => isEnabled)
  const sortedByLandingPage = sortBy(enabledLinks, ({ isLandingPage }) =>
    isLandingPage ? 0 : 1
  )

  if (isLoading) return <NavigationSkeleton />

  return (
    <div className={s.navigationBar}>
      <nav className={s.navigation}>
        <ul className={s.navigationLinks}>
          {sortedByLandingPage.map(({ ...props }) => (
            <NavigationItem key={props.href} {...props} />
          ))}
          {showAIChatButton && <AIChatButton />}
        </ul>
      </nav>
    </div>
  )
}

export const AIChatButton = () => {
  const { t } = useTranslation()
  const { openChat, chatConfig } = useAIChatContext()
  const { isBigWindow } = useWindowWidth()

  if (!chatConfig) return null

  const label = t("navigation.ASSISTANT")

  // On desktop the Assistant is a standalone blue dot pinned to the right,
  // out of flow so it does not shift the centred tabs; on tablet/mobile it
  // sits in the row as a plain tab.
  if (isBigWindow) {
    return (
      <li className={s.chatButtonItem}>
        <button
          className={s.chatButton}
          type="button"
          onClick={openChat}
          aria-label={label}
        >
          <Icon icon="AIChat" size="large" />
        </button>
      </li>
    )
  }

  return (
    <li>
      <button
        className={s.link}
        type="button"
        onClick={openChat}
        aria-label={label}
      >
        <span className={s.link__iconSlot}>
          <Icon icon="AIChat" size="extraLarge" className={s.link__icon} />
        </span>
        <div className={s.link__text}>{label}</div>
      </button>
    </li>
  )
}

const NavigationItem = ({
  href,
  text,
  icon,
  hasBadge = false,
}: {
  href: string
  icon: IconType
  text: string
  /** A dot over the icon: something is waiting behind this tab. */
  hasBadge?: boolean
}) => {
  return (
    <li>
      <Link
        to={href}
        search={{}}
        className={s.link}
        getActiveProps={getActiveProps}
        aria-label={text}
      >
        <span className={s.link__iconSlot}>
          <Icon icon={icon} size="extraLarge" className={s.link__icon} />
          {hasBadge && <span className={s.link__badge} />}
        </span>
        <div className={s.link__text}>{text}</div>
      </Link>
    </li>
  )
}

const getActiveProps = () => ({
  className: s["link--active"],
  "aria-current": "page",
})
